/**
 * Focused student editors for /family.
 *
 * The My Family student card now uses record-style blocks. Each block
 * owns its own underlined Edit action, and this sheet renders only the
 * fields for that block.
 */

import { Alert } from '@ordo/ui/alert'
import { Button } from '@ordo/ui/button'
import { useDestructiveConfirm } from '@ordo/ui/destructive-confirm'
import { FormField } from '@ordo/ui/form-field'
import { FormSheet } from '@ordo/ui/form-sheet'
import { Input } from '@ordo/ui/input'
import { Label } from '@ordo/ui/label'
import { RadioGroup, RadioGroupItem } from '@ordo/ui/radio-group'
import { SearchableSelect, type SearchableSelectOption } from '@ordo/ui/searchable-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'
import { toast } from '@ordo/ui/sonner'
import { Switch } from '@ordo/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@ordo/ui/tabs'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'

import { useBillingFamilyQuery, useSetAutoReloadMutation } from '@/api/billing-family'
import {
  type MyFamilyMember,
  type StudentEnrollmentFormFact,
  useFamilyClaimSchoolsQuery,
  useFamilyEnrollmentOptionsQuery,
  useGrantStudentSignInMutation,
  useRemoveFamilyStudentMutation,
  useRevokeStudentSignInMutation,
  useUpdateFamilyStudentMutation,
} from '@/api/family'
import { DEFAULT_AUTO_RELOAD_AMOUNT_CENTS, DEFAULT_AUTO_RELOAD_THRESHOLD_CENTS } from '@/components/billing/helpers'
import { UserAvatarColorPicker } from '@/components/user-avatar-color-picker'
import { formatCents } from '@/lib/school-orders/money'
import { DEFAULT_USER_AVATAR_COLOR, type UserAvatarColor } from '@/lib/user-avatar-colors'

import { getDisplayName, isValidMonthDay } from './helpers'

export type EditStudentSection = 'profile' | 'dietary' | 'spending'

export interface EditStudentSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  member: MyFamilyMember | null
  section: EditStudentSection
  allergenOptions: SearchableSelectOption[]
  dietaryOptions: SearchableSelectOption[]
}

export interface EditFormState {
  firstName: string
  lastName: string
  preferredName: string
  birthMonth: string
  birthDay: string
  avatarColor: UserAvatarColor
  /** Campus pick for students whose school link has no campus yet (set-once). */
  schoolLocationId: string
  homeroom: string
  gradeLevel: string
  teacher: string
  roomNumber: string
  lunchPeriod: string
  allergenTagIds: string[]
  dietaryTagIds: string[]
  /** Spending tab (POS) — this household's opt-in + daily cap + payer pick. */
  posOptIn: boolean
  /** Dollars, as typed. Empty = no daily limit. Converted to cents on submit. */
  spendingLimitDollars: string
  posPayer: 'PRIMARY' | 'ALTERNATE'
  /**
   * PROD-1119 — wallet auto-charge for the HOUSEHOLD (not the student), saved
   * through the billing auto-reload endpoint rather than the student PATCH.
   *
   * Seeded ON ("must enable auto charge by default"), but that only ever
   * WRITES when the nudge was on screen — and the nudge renders solely for a
   * household whose auto-charge is off. The write is further gated by the
   * sheet's dirty check, so opening Spending and touching nothing changes no
   * billing state; leaving the visible, labeled switch checked while saving
   * an edit is the consenting act.
   */
  autoCharge: boolean
}

/** Helper copy for the daily-limit field when the household set its own. */
const DAILY_LIMIT_HELPER_OWN = 'Empty = no daily limit. Resets each school day.'

/** Facts with dedicated UI (school/grade/homeroom rows) — everything else renders generically. */
const DEDICATED_FACTS = new Set(['GRADE', 'HOMEROOM'])

const FACT_FORM_KEY: Partial<Record<string, 'teacher' | 'roomNumber' | 'lunchPeriod'>> = {
  TEACHER: 'teacher',
  ROOM: 'roomNumber',
  LUNCH_PERIOD: 'lunchPeriod',
}

const MONTHS: { value: string; label: string }[] = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
]

function initialFrom(member: MyFamilyMember | null): EditFormState {
  const studentMembership = getStudentSchoolMembership(member)
  const enrollment = getCurrentEnrollment(member, studentMembership?.locationId ?? null)
  const birthday = birthdayParts(member?.user?.birthday)
  return {
    firstName: member?.user?.firstName ?? '',
    lastName: member?.user?.lastName ?? '',
    preferredName: member?.user?.preferredName ?? '',
    birthMonth: birthday.month,
    birthDay: birthday.day,
    avatarColor: member?.user?.avatarColor ?? DEFAULT_USER_AVATAR_COLOR,
    schoolLocationId: '',
    homeroom: enrollment?.homeroom ?? '',
    gradeLevel: enrollment?.gradeLevel ?? '',
    teacher: member?.user?.facts?.teacher ?? '',
    roomNumber: member?.user?.facts?.roomNumber ?? '',
    lunchPeriod: member?.user?.facts?.lunchPeriod ?? '',
    allergenTagIds: member?.user?.allergenTags.map((tag) => tag.id) ?? [],
    dietaryTagIds: member?.user?.dietaryTags.map((tag) => tag.id) ?? [],
    posOptIn: member?.posOptIn ?? false,
    spendingLimitDollars:
      typeof member?.spendingLimitCents === 'number' ? (member.spendingLimitCents / 100).toFixed(2) : '',
    posPayer: member?.user?.pos?.payerHousehold ?? 'PRIMARY',
    // Default ON (PROD-1119) — the real household value is folded in once the
    // billing query resolves (see the effect below), so a household that
    // already turned auto-charge OFF is never silently flipped back on.
    autoCharge: true,
  }
}

function getStudentSchoolMembership(member: MyFamilyMember | null) {
  return (
    member?.user?.memberships.find(
      (membership) =>
        membership.profileCode === 'STUDENT' &&
        !membership.isArchived &&
        typeof membership.organizationId === 'string' &&
        typeof membership.locationId === 'string',
    ) ?? null
  )
}

/** The student's school link regardless of campus — students added while the
 *  campus was still a draft carry an org-only membership (locationId null). */
function getStudentOrgMembership(member: MyFamilyMember | null) {
  return (
    member?.user?.memberships.find(
      (membership) =>
        membership.profileCode === 'STUDENT' && !membership.isArchived && typeof membership.organizationId === 'string',
    ) ?? null
  )
}

function getCurrentEnrollment(member: MyFamilyMember | null, locationId: string | null) {
  return member?.user?.enrollments.find((enrollment) => !locationId || enrollment.locationId === locationId) ?? null
}

export function mergeLinkedTagOptions(
  options: SearchableSelectOption[],
  linkedTags: ReadonlyArray<{ id: string; name: string }> | undefined,
): SearchableSelectOption[] {
  const known = new Set(options.map((option) => option.value))
  const extras = (linkedTags ?? [])
    .filter((tag) => !known.has(tag.id))
    .map((tag) => ({ value: tag.id, label: tag.name }))
  return extras.length > 0 ? [...options, ...extras] : options
}

/**
 * True when any field differs from the seeded baseline — drives the
 * dirty-gated Save. Covers every tab's fields (profile, dietary, spending);
 * array fields (allergen/dietary tag ids) compare element-wise.
 */
export function isEditFormDirty(form: EditFormState, baseline: EditFormState): boolean {
  return (Object.keys(baseline) as (keyof EditFormState)[]).some((key) => {
    const current = form[key]
    const seeded = baseline[key]
    if (Array.isArray(current) && Array.isArray(seeded)) {
      return current.length !== seeded.length || current.some((value, index) => value !== seeded[index])
    }
    return current !== seeded
  })
}

function birthdayParts(birthday: string | Date | null | undefined): { month: string; day: string } {
  if (!birthday) {
    return { month: '', day: '' }
  }
  const date = birthday instanceof Date ? birthday : new Date(birthday)
  if (Number.isNaN(date.getTime())) {
    return { month: '', day: '' }
  }
  return {
    month: String(date.getUTCMonth() + 1),
    day: String(date.getUTCDate()),
  }
}

export function EditStudentSheet({
  open,
  onOpenChange,
  member,
  section,
  allergenOptions,
  dietaryOptions,
}: EditStudentSheetProps) {
  const [form, setForm] = useState<EditFormState>(() => initialFrom(member))
  // The dirty baseline — the same seed the member/section effect resets the
  // form to. The campus-prefill effect intentionally counts as dirty: a
  // confirm-your-campus sheet must be saveable without touching other fields.
  const baseline = useMemo(() => initialFrom(member), [member])
  const [errors, setErrors] = useState<Partial<Record<keyof EditFormState, string>>>({})
  // The sheet opens on the section the caller asked for, but both sections
  // stay reachable via tabs — the compact student rows only deep-link to
  // 'profile', and dietary must never be a dead end.
  const [activeSection, setActiveSection] = useState<EditStudentSection>(section)
  const studentMembership = getStudentSchoolMembership(member)
  const orgMembership = getStudentOrgMembership(member)
  const enrollment = getCurrentEnrollment(member, studentMembership?.locationId ?? null)
  // The campus is parent-editable in two cases: a school link with no campus
  // (student added while the campus was still a draft — fill it), or an
  // auto-assigned campus that needs confirmation now that the school has
  // multiple campuses (confirm or re-pick). A confirmed campus stays
  // school-managed and read-only.
  const needsSchoolLocation = Boolean(orgMembership && !studentMembership)
  const needsCampusConfirmation = member?.user?.needsCampusConfirmation ?? false
  const campusEditable = needsSchoolLocation || needsCampusConfirmation
  const claimSchoolsQuery = useFamilyClaimSchoolsQuery({ enabled: campusEditable })
  const campusOptions = useMemo<SearchableSelectOption[]>(() => {
    if (!campusEditable || !orgMembership) {
      return []
    }
    const school = claimSchoolsQuery.data?.schools.find((candidate) => candidate.id === orgMembership.organizationId)
    return (school?.locations ?? []).map((location) => ({ value: location.id, label: location.name }))
  }, [campusEditable, orgMembership, claimSchoolsQuery.data])
  // The campus the pickers are scoped to: the parent's in-sheet pick when the
  // campus is editable, else the membership's.
  const effectiveLocationId =
    campusEditable && form.schoolLocationId ? form.schoolLocationId : (studentMembership?.locationId ?? null)
  const optionsQuery = useFamilyEnrollmentOptionsQuery(
    orgMembership?.organizationId && effectiveLocationId
      ? { organizationId: orgMembership.organizationId, schoolLocationId: effectiveLocationId }
      : null,
  )
  // Catalogs are pinned to the canonical contains-policy tags, but a student
  // may still carry legacy tags linked before the catalog was canonicalized.
  // Merge those in so their chips render labeled and stay removable.
  const mergedAllergenOptions = useMemo(
    () => mergeLinkedTagOptions(allergenOptions, member?.user?.allergenTags),
    [allergenOptions, member],
  )
  const mergedDietaryOptions = useMemo(
    () => mergeLinkedTagOptions(dietaryOptions, member?.user?.dietaryTags),
    [dietaryOptions, member],
  )

  const updateStudentMutation = useUpdateFamilyStudentMutation()
  const removeStudentMutation = useRemoveFamilyStudentMutation()
  const grantSignInMutation = useGrantStudentSignInMutation()
  const revokeSignInMutation = useRevokeStudentSignInMutation()
  const setAutoReloadMutation = useSetAutoReloadMutation()
  const confirmDestructive = useDestructiveConfirm()

  // Spending tab (POS, PROD-227 PR4) — the server projects `pos` only when
  // the student's campus resolves POS-enabled; without it the tab (and the
  // PATCH fields) never render. Payer designation is primary-guardian-of-
  // primary-household only, and only once both households are opted in.
  const pos = member?.user?.pos ?? null
  const showSpendingTab = Boolean(pos?.campusPosEnabled)
  const canDesignatePayer = Boolean(
    pos?.dualHousehold &&
    pos.viewerIsPrimaryHousehold &&
    pos.viewerIsPrimaryGuardian &&
    form.posOptIn &&
    pos.alternateOptedIn,
  )
  // Balance cross-link + the auto-charge nudge. The sheet still hosts no
  // payment UI: enabling auto-charge needs a saved default method, and without
  // one the nudge is a link to /billing. The first household is "the"
  // household, matching the /billing convention.
  const billingQuery = useBillingFamilyQuery({ first: 1 }, { enabled: showSpendingTab })
  const billingAccount = showSpendingTab ? (billingQuery.data?.me?.ownedAccounts[0] ?? null) : null
  const hasDefaultPaymentMethod = Boolean(billingAccount?.defaultPaymentMethodRef)
  // PROD-1119 — "auto charge on by default": show the nudge only while POS
  // applies to this student AND the household has no auto-charge yet. A
  // household that already has it on needs no prompt.
  const showAutoChargeNudge = Boolean(showSpendingTab && form.posOptIn && billingAccount && !billingAccount.autoReload)
  // The cap this student inherits when the household set none: the school
  // default at a default-opt-in campus, else no cap.
  const inheritedDailyLimitCents =
    pos?.campusDefaultOptIn && typeof pos.defaultDailyLimitCents === 'number' ? pos.defaultDailyLimitCents : null
  const inheritedDailyLimitHelper =
    inheritedDailyLimitCents === null
      ? DAILY_LIMIT_HELPER_OWN
      : `Empty = your school's ${formatCents(inheritedDailyLimitCents, 'USD')} daily limit. Resets each school day.`

  // Sign-in grant state (students without a sign-in only): toggle +
  // required email. The grant runs on Save, after the profile update.
  const [signInEnabled, setSignInEnabled] = useState(false)
  const [signInEmail, setSignInEmail] = useState('')
  const [signInError, setSignInError] = useState<string | null>(null)

  // Grades + homerooms come from the school's configured vocabulary; a
  // legacy value outside the list stays pickable.
  const savedGradeLevel = enrollment?.gradeLevel ?? ''
  const gradeOptions = useMemo(() => {
    const configured = optionsQuery.data?.gradeOptions ?? []
    if (savedGradeLevel && !configured.some((grade) => grade.toUpperCase() === savedGradeLevel.toUpperCase())) {
      return [savedGradeLevel, ...configured]
    }
    return configured
  }, [optionsQuery.data, savedGradeLevel])
  const savedHomeroom = enrollment?.homeroom ?? ''
  const homeroomOptions = useMemo(() => {
    const configured = optionsQuery.data?.homeroomOptions ?? []
    if (savedHomeroom && !configured.some((value) => value.toUpperCase() === savedHomeroom.toUpperCase())) {
      return [savedHomeroom, ...configured]
    }
    return configured
  }, [optionsQuery.data, savedHomeroom])
  // A stored legacy value keeps its option list non-empty, so the length test
  // alone would still show a picker for a fact the campus marks HIDDEN. Such a
  // fact is absent from the fact form, which is the campus list of what it
  // collects (PROD-1229). A pre-upgrade cached response has no form at all —
  // fall back to the length test rather than hiding a picker that should show.
  const collectedFacts = optionsQuery.data?.factForm?.facts
  const collects = (fact: string) => !collectedFacts || collectedFacts.some((entry) => entry.fact === fact)
  const showGradePicker = gradeOptions.length > 0 && collects('GRADE')
  const showHomeroomPicker = homeroomOptions.length > 0 && collects('HOMEROOM')
  // Campus facts beyond grade/homeroom (teacher, classroom, lunch period) —
  // whatever the campus collects (non-HIDDEN) and has values for.
  const extraFacts = useMemo<StudentEnrollmentFormFact[]>(
    () => (optionsQuery.data?.factForm?.facts ?? []).filter((fact) => fact.askable && !DEDICATED_FACTS.has(fact.fact)),
    [optionsQuery.data],
  )

  // Preselect the campus pick: the current (auto-assigned) campus when
  // confirming, or the lone visible campus when filling a blank — so the
  // grade/fact pickers light up immediately and confirming is one tap.
  useEffect(() => {
    if (!campusEditable) {
      return
    }
    const prefill =
      (needsCampusConfirmation ? studentMembership?.locationId : null) ??
      (campusOptions.length === 1 ? campusOptions[0]?.value : null)
    if (prefill) {
      setForm((prev) => (prev.schoolLocationId ? prev : { ...prev, schoolLocationId: prefill }))
    }
  }, [campusEditable, needsCampusConfirmation, studentMembership?.locationId, campusOptions])

  useEffect(() => {
    setForm(initialFrom(member))
    setErrors({})
    // A 'spending' deep-link only holds when the server projected the pos
    // block — otherwise fall back to profile (tab hidden, never a dead end).
    setActiveSection(section === 'spending' && !member?.user?.pos?.campusPosEnabled ? 'profile' : section)
    // The grant toggle ALWAYS starts off — an imported/stored student email
    // must never pre-enable it. The stored email only prefills the field
    // once the parent actively turns the toggle on (see onCheckedChange).
    setSignInEnabled(false)
    setSignInEmail('')
    setSignInError(null)
  }, [member, section])

  function update<K extends keyof EditFormState>(key: K, value: EditFormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => {
      if (!prev[key]) {
        return prev
      }
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  function validateProfile(): boolean {
    const next: Partial<Record<keyof EditFormState, string>> = {}
    if (!form.firstName.trim()) {
      next.firstName = 'Required'
    }
    if (!form.lastName.trim()) {
      next.lastName = 'Required'
    }
    const monthNum = form.birthMonth ? Number(form.birthMonth) : null
    const dayNum = form.birthDay ? Number(form.birthDay) : null
    if ((monthNum == null) !== (dayNum == null)) {
      next.birthDay = 'Birthday needs both month + day, or neither.'
    } else if (!isValidMonthDay(monthNum, dayNum)) {
      next.birthDay = "That date doesn't exist — double-check the month + day."
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  if (!member?.user) {
    return null
  }

  const displayName = getDisplayName(member.user)
  const studentUserId = member.user.id
  const hasSignIn = member.user.canSignIn
  const signInEmailOnFile = member.user.email
  const title = `Edit ${displayName}`
  // Save is gated on an actual change: any form field differing from the
  // seeded baseline, or arming the sign-in grant (a save-worthy action that
  // lives outside the form state).
  const isDirty = isEditFormDirty(form, baseline) || (!hasSignIn && signInEnabled)
  const description =
    activeSection === 'profile'
      ? 'Update student profile details and school info.'
      : activeSection === 'spending'
        ? 'School spending settings — cafeteria register charges land on your household wallet.'
        : "For your family's reference only — these don't filter menus, block meals, or affect auto-orders. Medically required diets need a special diet approval."
  const schoolLabel = studentMembership?.location?.name ?? enrollment?.locationName ?? '--'
  const gradeLabel = enrollment?.gradeLevel ?? '--'

  async function handleSubmit() {
    // Save persists BOTH tabs in one PATCH — a parent who edits the profile,
    // switches to Dietary, and hits Save must never silently lose either half.
    if (!validateProfile()) {
      setActiveSection('profile')
      throw new Error('Fix the highlighted fields and try again.')
    }
    // Spending tab: dollars → cents (empty = no daily limit). Rejected
    // before the PATCH so a typo never clears a real limit server-side.
    let spendingLimitCents: number | null = null
    if (pos) {
      const rawLimit = form.spendingLimitDollars.trim()
      if (rawLimit !== '') {
        const dollars = Number(rawLimit)
        if (!Number.isFinite(dollars) || dollars < 0) {
          setActiveSection('spending')
          setErrors((prev) => ({ ...prev, spendingLimitDollars: 'Enter a dollar amount of $0.00 or more.' }))
          throw new Error('Fix the highlighted fields and try again.')
        }
        spendingLimitCents = Math.round(dollars * 100)
      }
    }
    const grantEmail = signInEmail.trim()
    const wantsSignIn = !hasSignIn && signInEnabled
    if (wantsSignIn && !grantEmail.includes('@')) {
      setActiveSection('profile')
      const message = 'Enter the email this student will sign in with.'
      setSignInError(message)
      throw new Error(message)
    }
    // The campus scope for school-side writes: the parent's in-sheet pick
    // when editable (the server sets/confirms it first), else the
    // membership's campus.
    const submitLocationId =
      campusEditable && form.schoolLocationId ? form.schoolLocationId : (studentMembership?.locationId ?? '')
    await updateStudentMutation.mutateAsync({
      studentUserId,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      preferredName: form.preferredName.trim() || null,
      birthMonth: form.birthMonth || null,
      birthDay: form.birthDay || null,
      avatarColor: form.avatarColor,
      allergenTagIds: form.allergenTagIds,
      dietaryTagIds: form.dietaryTagIds,
      ...(orgMembership?.organizationId && submitLocationId
        ? {
            organizationId: orgMembership.organizationId,
            schoolLocationId: submitLocationId,
            ...(showGradePicker && form.gradeLevel ? { gradeLevel: form.gradeLevel } : {}),
            ...(showHomeroomPicker ? { homeroom: form.homeroom || null } : {}),
            ...(extraFacts.some((fact) => FACT_FORM_KEY[fact.fact] === 'teacher') && form.teacher
              ? { teacher: form.teacher }
              : {}),
            ...(extraFacts.some((fact) => FACT_FORM_KEY[fact.fact] === 'roomNumber') && form.roomNumber
              ? { roomNumber: form.roomNumber }
              : {}),
            ...(extraFacts.some((fact) => FACT_FORM_KEY[fact.fact] === 'lunchPeriod') && form.lunchPeriod
              ? { lunchPeriod: form.lunchPeriod }
              : {}),
          }
        : {}),
      // Spending fields ride the same single PATCH (server re-gates on
      // effective POS). Payer designation is sent only when this viewer may
      // actually set it — alternates and non-primary guardians never PATCH it.
      ...(pos
        ? {
            posOptIn: form.posOptIn,
            spendingLimitCents,
            ...(canDesignatePayer ? { posPayer: form.posPayer } : {}),
          }
        : {}),
    })
    // PROD-1119 — auto-charge lives on the HOUSEHOLD, so it is a second write
    // through the billing endpoint. Fired only when the nudge was on screen and
    // the parent left it enabled; a failure here must not lose the profile save,
    // so it surfaces as a warning rather than rejecting the submit.
    if (showAutoChargeNudge && form.autoCharge && hasDefaultPaymentMethod && billingAccount) {
      try {
        await setAutoReloadMutation.mutateAsync({
          // ponytail: the household whose balance this tab SHOWED. Still the
          // first owned account, which is wrong for a parent who owns two —
          // but now the write lands on the one the nudge was rendered from
          // instead of on whichever is oldest. Resolving the student's own
          // household is the real fix (PROD-1314).
          accountId: billingAccount.id,
          enabled: true,
          thresholdCents: DEFAULT_AUTO_RELOAD_THRESHOLD_CENTS,
          reloadAmountCents: DEFAULT_AUTO_RELOAD_AMOUNT_CENTS,
        })
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Could not turn on auto-reload.')
      }
    }
    if (wantsSignIn) {
      // Grant AFTER the profile save so a 409 (email in use) keeps the
      // saved fields and surfaces inline; resubmitting retries the grant.
      await grantSignInMutation.mutateAsync({ studentUserId, email: grantEmail })
      toast.success(`Saved ${displayName}`, {
        description: `Sign-in created — ${grantEmail} can now order for themselves.`,
      })
      return
    }
    // Generic on purpose — the save PATCHes whichever tabs changed (profile,
    // dietary, spending), so naming two of them here would go stale again.
    toast.success('Changes saved.')
  }

  async function handleRemoveSignIn() {
    const ok = await confirmDestructive({
      title: `Remove sign-in for ${displayName}?`,
      body: 'They will no longer be able to sign in or place their own orders. Their profile, meals, and order history stay on your household.',
      confirmLabel: 'Remove sign-in',
      intent: 'delete',
    })
    if (!ok) {
      return
    }
    try {
      await revokeSignInMutation.mutateAsync(studentUserId)
      toast.success(`Removed sign-in for ${displayName}`)
    } catch (err) {
      toast.error("Couldn't remove sign-in", {
        description: err instanceof Error ? err.message : 'Please try again.',
      })
    }
  }

  async function handleRemove() {
    const ok = await confirmDestructive({
      title: `Remove ${displayName}?`,
      body: "This removes them from your family and their meal ordering. If they're your last student and you're a staff member, you'll return to a staff-only account. You can add them again later.",
      confirmLabel: 'Remove student',
      intent: 'delete',
    })
    if (!ok) {
      return
    }
    try {
      await removeStudentMutation.mutateAsync(studentUserId)
      toast.success(`Removed ${displayName}`)
      onOpenChange(false)
    } catch (err) {
      toast.error("Couldn't remove student", {
        description: err instanceof Error ? err.message : 'Please try again.',
      })
    }
  }

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      submitLabel={!hasSignIn && signInEnabled ? 'Save & enable sign-in' : 'Save changes'}
      submitDisabled={!isDirty}
      onSubmit={handleSubmit}
      onReset={() => setForm(initialFrom(member))}
      size="lg">
      <Tabs value={activeSection} onValueChange={(value) => setActiveSection(value as EditStudentSection)}>
        <TabsList className={showSpendingTab ? 'grid w-full grid-cols-3' : 'grid w-full grid-cols-2'}>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="dietary">Dietary</TabsTrigger>
          {showSpendingTab ? <TabsTrigger value="spending">Spending</TabsTrigger> : null}
        </TabsList>
        <TabsContent value="profile" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField label="First name" htmlFor="edit-student-first" error={errors.firstName}>
              <Input
                id="edit-student-first"
                value={form.firstName}
                onChange={(e) => update('firstName', e.target.value)}
                autoComplete="given-name"
                placeholder="Samuel"
              />
            </FormField>
            <FormField label="Last name" htmlFor="edit-student-last" error={errors.lastName}>
              <Input
                id="edit-student-last"
                value={form.lastName}
                onChange={(e) => update('lastName', e.target.value)}
                autoComplete="family-name"
                placeholder="Park"
              />
            </FormField>
          </div>

          <FormField
            label="Preferred name"
            htmlFor="edit-student-preferred"
            helperText="Falls back to the legal first name when empty.">
            <Input
              id="edit-student-preferred"
              value={form.preferredName}
              onChange={(e) => update('preferredName', e.target.value)}
              placeholder="Sammy"
            />
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              label="Birth month"
              htmlFor="edit-student-birth-month"
              helperText="Optional — we use this to send birthday specials. We don't collect the year.">
              <Select value={form.birthMonth} onValueChange={(v) => update('birthMonth', v === '__none__' ? '' : v)}>
                <SelectTrigger id="edit-student-birth-month">
                  <SelectValue placeholder="Pick a month" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Not set</SelectItem>
                  {MONTHS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Birth day" htmlFor="edit-student-birth-day" error={errors.birthDay}>
              <Input
                id="edit-student-birth-day"
                type="number"
                inputMode="numeric"
                min={1}
                max={31}
                value={form.birthDay}
                onChange={(e) => update('birthDay', e.target.value)}
                placeholder="12"
              />
            </FormField>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {campusEditable && campusOptions.length > 0 ? (
              <FormField
                label="School"
                helperText={
                  needsCampusConfirmation
                    ? 'Confirm which campus your student attends.'
                    : 'Choose the campus your student attends.'
                }>
                <SearchableSelect
                  placeholder={claimSchoolsQuery.isLoading ? 'Loading campuses…' : 'Choose a campus'}
                  options={campusOptions}
                  value={form.schoolLocationId || null}
                  onChange={(value) => update('schoolLocationId', value ?? '')}
                />
              </FormField>
            ) : (
              <FormField label="School" htmlFor="edit-student-school" helperText="Set by the student's school link.">
                <Input id="edit-student-school" value={schoolLabel} readOnly />
              </FormField>
            )}
            {showGradePicker ? (
              <FormField label="Grade" htmlFor="edit-student-grade">
                <Select
                  value={form.gradeLevel || '__none__'}
                  onValueChange={(value) => update('gradeLevel', value === '__none__' ? '' : value)}>
                  <SelectTrigger id="edit-student-grade">
                    <SelectValue placeholder="Pick a grade" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Not set</SelectItem>
                    {gradeOptions.map((grade) => (
                      <SelectItem key={grade} value={grade}>
                        {grade}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
            ) : (
              <FormField label="Grade" htmlFor="edit-student-grade" helperText="Set by the student's school.">
                <Input id="edit-student-grade" value={gradeLabel} readOnly />
              </FormField>
            )}
          </div>

          {showHomeroomPicker ? (
            <FormField label="Homeroom" htmlFor="edit-student-homeroom">
              <Select
                value={form.homeroom || '__none__'}
                onValueChange={(value) => update('homeroom', value === '__none__' ? '' : value)}>
                <SelectTrigger id="edit-student-homeroom">
                  <SelectValue placeholder={optionsQuery.isLoading ? 'Loading homerooms...' : 'Pick a homeroom'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Not set</SelectItem>
                  {homeroomOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          ) : null}

          {extraFacts.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {extraFacts.map((fact) => {
                const key = FACT_FORM_KEY[fact.fact]
                if (!key) {
                  return null
                }
                const fieldId = `edit-student-fact-${fact.fact.toLowerCase()}`
                const current = form[key]
                // A stored legacy value outside today's list stays pickable.
                const options =
                  current && !fact.options.some((option) => option.toUpperCase() === current.toUpperCase())
                    ? [current, ...fact.options]
                    : fact.options
                return (
                  <FormField key={fact.fact} label={fact.label} htmlFor={fieldId}>
                    <Select value={current || '__none__'} onValueChange={(v) => update(key, v === '__none__' ? '' : v)}>
                      <SelectTrigger id={fieldId}>
                        <SelectValue placeholder={`Pick a ${fact.label.toLowerCase()}`} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Not set</SelectItem>
                        {options.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                )
              })}
            </div>
          ) : null}

          <UserAvatarColorPicker
            value={form.avatarColor}
            onChange={(avatarColor) => update('avatarColor', avatarColor)}
          />

          <div className="mt-2 flex flex-col gap-3 border-t border-border pt-4">
            {hasSignIn ? (
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground">Sign-in enabled</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {signInEmailOnFile ?? 'No email on file'} — they can browse the menu and place orders that charge
                    your household.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0 text-destructive hover:text-destructive"
                  onClick={() => void handleRemoveSignIn()}
                  disabled={revokeSignInMutation.isPending}>
                  {revokeSignInMutation.isPending ? 'Removing…' : 'Remove sign-in'}
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground">Let this student order for themselves</p>
                    <p className="text-xs text-muted-foreground">
                      They&apos;ll sign in with this email and can browse the menu and place orders that charge your
                      household.
                    </p>
                  </div>
                  <Switch
                    id="edit-student-signin-toggle"
                    checked={signInEnabled}
                    onCheckedChange={(checked) => {
                      setSignInEnabled(checked)
                      setSignInError(null)
                      // Stored email prefills the FIELD only on an explicit
                      // toggle-on — it never pre-enables the toggle itself.
                      if (checked && signInEmail.trim() === '' && signInEmailOnFile) {
                        setSignInEmail(signInEmailOnFile)
                      }
                    }}
                    aria-label="Let this student order for themselves"
                  />
                </div>
                {signInEnabled ? (
                  <>
                    <Alert variant="warning">
                      {displayName} will be able to place orders that charge your household balance.
                    </Alert>
                    <FormField
                      label="Student email"
                      htmlFor="edit-student-signin-email"
                      error={signInError ?? undefined}>
                      <Input
                        id="edit-student-signin-email"
                        type="email"
                        value={signInEmail}
                        onChange={(e) => {
                          setSignInEmail(e.target.value)
                          setSignInError(null)
                        }}
                        autoComplete="email"
                        placeholder="student@example.com"
                      />
                    </FormField>
                  </>
                ) : null}
              </>
            )}
          </div>

          <div className="mt-2 border-t border-border pt-4">
            <Button
              type="button"
              variant="ghost"
              className="text-destructive hover:text-destructive"
              onClick={() => void handleRemove()}
              disabled={removeStudentMutation.isPending}>
              {removeStudentMutation.isPending ? 'Removing…' : 'Remove student'}
            </Button>
          </div>
        </TabsContent>
        <TabsContent value="dietary" className="mt-4 space-y-4">
          <SearchableSelect
            multi
            label="Allergens"
            placeholder="Search peanut, dairy, sesame..."
            options={mergedAllergenOptions}
            value={form.allergenTagIds}
            onChange={(next) => update('allergenTagIds', next)}
          />

          <SearchableSelect
            multi
            label="Dietary preferences"
            placeholder="Search halal, kosher, vegan..."
            options={mergedDietaryOptions}
            value={form.dietaryTagIds}
            onChange={(next) => update('dietaryTagIds', next)}
          />

          {member.user.medicalDocumentationAvailable ? (
            <div className="rounded-md border border-border bg-muted/20 p-3 text-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-foreground">Medical documentation</p>
                  <p className="mt-1 text-muted-foreground">
                    Approved special diets are enforced when meals are ordered and served — and can unlock special-diet
                    items.
                  </p>
                </div>
                <Link
                  to={`/family/medical/${encodeURIComponent(member.user.id)}`}
                  className="shrink-0 font-medium text-foreground underline underline-offset-2 hover:text-muted-foreground">
                  Manage
                </Link>
              </div>
            </div>
          ) : null}
        </TabsContent>
        {showSpendingTab && pos ? (
          <TabsContent value="spending" className="mt-4 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <Label htmlFor="edit-student-pos-optin" className="text-sm font-medium text-foreground">
                  Let {displayName} charge meals at school
                </Label>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {pos.campusDefaultOptIn
                    ? `Items rung up in the lunch line charge your household wallet. Your school turns this on for every student. Turn it off to stop charges for ${displayName}.`
                    : 'Items rung up in the lunch line charge your household wallet.'}
                </p>
              </div>
              <Switch
                id="edit-student-pos-optin"
                checked={form.posOptIn}
                onCheckedChange={(checked) => update('posOptIn', checked)}
                aria-label={`Let ${displayName} charge meals at school`}
              />
            </div>

            {form.posOptIn ? (
              <>
                <FormField
                  label="Daily spending limit ($)"
                  htmlFor="edit-student-pos-limit"
                  error={errors.spendingLimitDollars}
                  helperText={inheritedDailyLimitCents === null ? DAILY_LIMIT_HELPER_OWN : inheritedDailyLimitHelper}>
                  <Input
                    id="edit-student-pos-limit"
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="0.01"
                    value={form.spendingLimitDollars}
                    onChange={(e) => update('spendingLimitDollars', e.target.value)}
                    placeholder={
                      inheritedDailyLimitCents === null ? '10.00' : (inheritedDailyLimitCents / 100).toFixed(2)
                    }
                  />
                </FormField>

                <Alert variant="info">
                  If your wallet runs low, charges can take your balance to{' '}
                  {formatCents(-pos.negativeFloorCents, 'USD')} before the register declines.
                </Alert>

                {showAutoChargeNudge ? (
                  <div className="space-y-2 rounded-md border border-border p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <Label htmlFor="edit-student-pos-autocharge" className="text-sm font-medium text-foreground">
                          Keep the wallet topped up automatically
                        </Label>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {hasDefaultPaymentMethod
                            ? `We add ${formatCents(DEFAULT_AUTO_RELOAD_AMOUNT_CENTS, 'USD')} whenever the balance drops below ${formatCents(DEFAULT_AUTO_RELOAD_THRESHOLD_CENTS, 'USD')}, so a charge at the register never declines.`
                            : 'Add a payment method first, then we can top the wallet up before a charge declines.'}
                        </p>
                      </div>
                      {hasDefaultPaymentMethod ? (
                        <Switch
                          id="edit-student-pos-autocharge"
                          checked={form.autoCharge}
                          onCheckedChange={(checked) => update('autoCharge', checked)}
                          aria-label="Keep the wallet topped up automatically"
                        />
                      ) : (
                        <Link
                          to="/billing"
                          className="shrink-0 text-sm font-medium text-foreground underline underline-offset-2 hover:text-muted-foreground">
                          Add
                        </Link>
                      )}
                    </div>
                  </div>
                ) : null}

                {pos.dualHousehold ? (
                  canDesignatePayer ? (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-foreground">Which household pays?</p>
                      <RadioGroup
                        value={form.posPayer}
                        onValueChange={(value) => update('posPayer', value as 'PRIMARY' | 'ALTERNATE')}>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="PRIMARY" id="edit-student-pos-payer-primary" />
                          <Label htmlFor="edit-student-pos-payer-primary" className="font-normal">
                            Ours (primary)
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="ALTERNATE" id="edit-student-pos-payer-alternate" />
                          <Label htmlFor="edit-student-pos-payer-alternate" className="font-normal">
                            {pos.alternateHouseholdName ?? 'The other household'}
                          </Label>
                        </div>
                      </RadioGroup>
                      <p className="text-xs text-muted-foreground">
                        Only meaningful while both households have school spending on.
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Charged to:{' '}
                      {pos.payerHousehold === 'ALTERNATE'
                        ? (pos.alternateHouseholdName ?? 'the other household')
                        : 'primary household'}
                    </p>
                  )
                ) : null}
              </>
            ) : null}

            {billingAccount ? (
              <div className="mt-2 flex items-center justify-between gap-3 border-t border-border pt-4 text-sm">
                <span className="text-muted-foreground">
                  Current balance {formatCents(billingAccount.balanceCents, billingAccount.currency)}
                </span>
                <Link
                  to="/billing"
                  className="font-medium text-foreground underline underline-offset-2 hover:text-muted-foreground">
                  Add money
                </Link>
              </div>
            ) : null}
          </TabsContent>
        ) : null}
      </Tabs>
    </FormSheet>
  )
}
